import React, { useEffect } from 'react';
import { SceneState, useSceneStore, ColorScale } from '../../state/sceneStore';
import { formatInputNumber } from '../../utils/formatting';

// Helper to generate gradient for the color scale dropdown (reused from SimulationPanel)
// Helper to generate gradient for the color scale dropdown (reused from SimulationPanel)

const generateGradient = (colorScale: ColorScale): string => {
    // Basic gradient generation for the UI
    if (colorScale === 'blue-red') return 'linear-gradient(to right, rgb(0,0,255), rgb(255,0,0))';
    if (colorScale === 'jet') return 'linear-gradient(to right, rgb(0,0,127), rgb(0,0,255), rgb(0,255,255), rgb(0,255,0), rgb(255,255,0), rgb(255,0,0), rgb(127,0,0))';
    return 'linear-gradient(to right, #333, #ccc)'; // Generic fallback
};


const DemoPanel: React.FC = () => {
    const pointCloudVisible = useSceneStore((state: SceneState) => state.pointCloudVisible);
    const setPointCloudVisible = useSceneStore((state: SceneState) => state.setPointCloudVisible);
    const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
    const setUseOrthographic = useSceneStore((state: SceneState) => state.setUseOrthographic);
    const referenceImageVisible = useSceneStore((state: SceneState) => state.referenceImageVisible);
    const setReferenceImageVisible = useSceneStore((state: SceneState) => state.setReferenceImageVisible);
    const heatmapVisible = useSceneStore((state: SceneState) => state.heatmapVisible);
    const setHeatmapVisible = useSceneStore((state: SceneState) => state.setHeatmapVisible);
    const sceneOptions = useSceneStore((state: SceneState) => state.sceneOptions);
    const setSceneOptions = useSceneStore((state: SceneState) => state.setSceneOptions);

    // Force color scale to jet in demo mode
    useEffect(() => {
        if (sceneOptions.colorScale !== 'jet') {
            setSceneOptions(opts => ({ ...opts, colorScale: 'jet' }));
        }
    }, [sceneOptions.colorScale, setSceneOptions]);

    const panelStyle: React.CSSProperties = {
        background: 'rgba(25, 35, 45, 0.9)',
        color: 'white',
        padding: '15px',
        borderRadius: '8px',
        border: '1px solid #334155',
        marginBottom: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    };

    const buttonStyle: React.CSSProperties = {
        background: '#4f46e5',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        padding: '8px 12px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        transition: 'all 0.2s ease',
        flex: 1
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        boxSizing: 'border-box',
        background: '#334155',
        color: 'white',
        border: '1px solid #475569',
        borderRadius: '4px',
        padding: '4px 8px',
    };

    return (
        <div style={panelStyle}>
            <div>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Demo View Controls
                </h4>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <button
                        onClick={() => setUseOrthographic(false)}
                        style={{
                            ...buttonStyle,
                            background: !useOrthographic ? '#10b981' : '#4b5563'
                        }}
                    >
                        3D View
                    </button>
                    <button
                        onClick={() => setUseOrthographic(true)}
                        style={{
                            ...buttonStyle,
                            background: useOrthographic ? '#10b981' : '#4b5563'
                        }}
                    >
                        Top-Down
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                    <button
                        onClick={() => setPointCloudVisible(!pointCloudVisible)}
                        style={{
                            ...buttonStyle,
                            background: pointCloudVisible ? '#3b82f6' : '#4b5563'
                        }}
                    >
                        {pointCloudVisible ? 'Hide' : 'Show'} Point Cloud
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                        onClick={() => setReferenceImageVisible(!referenceImageVisible)}
                        style={{
                            ...buttonStyle,
                            background: referenceImageVisible ? '#3b82f6' : '#4b5563'
                        }}
                    >
                        {referenceImageVisible ? 'Hide' : 'Show'} Ref Image
                    </button>
                </div>
            </div>

            <div style={{ borderTop: '1px solid #475569', paddingTop: '15px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Heatmap
                </h4>

                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <button
                        onClick={() => setHeatmapVisible(!heatmapVisible)}
                        style={{
                            ...buttonStyle,
                            background: heatmapVisible ? '#3b82f6' : '#4b5563'
                        }}
                    >
                        {heatmapVisible ? 'Hide' : 'Show'} Heatmap
                    </button>
                </div>

                {heatmapVisible && (
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#cbd5e1' }}>Scale & Range</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="number"
                                value={formatInputNumber(sceneOptions.heatmapScale.min)}
                                onChange={(e) => setSceneOptions(opts => ({ heatmapScale: { ...opts.heatmapScale, min: Number(e.target.value) } }))}
                                style={{ ...inputStyle, width: '60px' }}
                            />

                            <div className="color-scale-display" style={{ position: 'relative', flex: 1 }}>
                                <div
                                    style={{
                                        height: '24px',
                                        background: generateGradient('jet'),
                                        borderRadius: '4px',
                                        border: '1px solid #475569',
                                    }}
                                    title="Color Scale (Jet)"
                                />
                            </div>

                            <input
                                type="number"
                                value={formatInputNumber(sceneOptions.heatmapScale.max)}
                                onChange={(e) => setSceneOptions(opts => ({ heatmapScale: { ...opts.heatmapScale, max: Number(e.target.value) } }))}
                                style={{ ...inputStyle, width: '60px' }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DemoPanel;
